import { buildURL } from 'react-imgix';

/**
 * Checks if a URL string is URL-encoded.
 * Returns true only if decoding changes the string AND
 * re-encoding the decoded value reproduces the original input.
 */
export const isUrlEncoded = (url: string): boolean => {
  try {
    const decoded = decodeURI(url);
    // Treat as URL-encoded only if decoding changes the string AND
    // re-encoding the decoded value reproduces the original input.
    if (decoded === url) {
      return false;
    }
    return encodeURI(decoded) === url;
  } catch {
    // If decoding fails, treat it as not-encoded to avoid errors elsewhere
    return false;
  }
};

/**
 * Removes invalid width and height params.
 * These invalid params cause the appended width and height parameters to be
 * ignored, resulting in really large images being loaded which degrades the
 * overall performance of the site.
 *
 * This is a temporary workaround until the backend changes can be made and
 * the listings can be completely re-indexed into elasticsearch.
 * @see https://getmyboat.slack.com/archives/G0WKAM32S/p1626272592033500
 * @param {string} src
 */
const stripBadHeightAndWidthParams = src => {
  if (src.includes('?') && src.includes('=None')) {
    const [domain, ...queryString] = src.split('?');
    return `${domain}?${queryString.join('').split('&').filter(s => !['h=None', 'w=None'].includes(s)).join('&')}`;
  }
  return src;
};

export const pointAtImgix = (src: string): string => {
  // eslint-disable-next-line no-param-reassign
  src = stripBadHeightAndWidthParams(src);

  if (src.startsWith('/')) {
    return `${__IMGIX_URL__}${src}`;
  }
  return src;
};

export const defaultImgixProps = {
  q: 50,
  lossless: false,
  fit: 'clip',
  auto: ['format', 'compress'],
};

// When added to the imgix params of an image,
// these produce a blurry blocky version of the
// image that's much smaller in terms of kb.
export const lqipParams = {
  blur: 200,
  px: 16,
};

export const normalizeImgixUrl = (url: string): string => {
  // Some image URLs come from backend already URL-encoded.
  // Imgix expects a "normal" (decoded) URL as input so
  // it can safely apply its own parameter encoding. We therefore decode only
  // when the source looks encoded, avoiding double-encoding issues while
  // preserving already-correct URLs.
  const normalizedUrl = isUrlEncoded(url) ? decodeURI(url) : url;
  return pointAtImgix(normalizedUrl);
};

// TODO; see if there's a less obfuscated way to do his logic.
export const buildImgixUrl = (src, options = {}) => (
  buildURL(normalizeImgixUrl(src), {
    ...defaultImgixProps,
    ...options,
    auto: [...defaultImgixProps.auto, ...(options.auto || [])],
  })
);

export const imgixParamsForLightbox = {
  w: typeof window !== 'undefined'
    // 90% of viewport width the same as `90vw` passed as width prop to Lightbox
    ? Math.round(document.body.clientWidth * 0.9)
    // Fallback to some meaningful size for mobiles
    : 400,
};
